import { useCallback, useEffect, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { authReducer, session } from '@bitstopco/bitstop-theme';

import { AUTH0_LOGIN_PATH } from '@/constants/app/config';
import { hasAuthorizationCode } from '@/helpers/auth0';
import { isAuth0Token, isTokenExpired, storeAuth0Session } from '@/helpers/auth0Session';
import { useAppDispatch } from '@/store';

const { actions: authActions } = authReducer;

const RENEWAL_CHECK_INTERVAL_MS = 60 * 1000;
const RENEWAL_WINDOW_MS = 5 * 60 * 1000;

const RESTORE_TIMEOUT_MS = 10 * 1000;

const useAuth0SessionSync = () => {
  const dispatch = useAppDispatch();

  const { isLoading, isAuthenticated, getAccessTokenSilently, getIdTokenClaims } = useAuth0();

  const [wasAuth0Session] = useState(() => isAuth0Token(session.getValue()));
  const [isRestoring, setIsRestoring] = useState(wasAuth0Session && !hasAuthorizationCode());

  const endSessionIfExpired = useCallback(() => {
    if (!isTokenExpired(session.getValue())) return;

    dispatch(authActions.logout());
  }, [dispatch]);

  const renew = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const idTokenClaims = await getIdTokenClaims();

      storeAuth0Session(accessToken, idTokenClaims?.__raw);
    } catch {
      endSessionIfExpired();
    }
  }, [getAccessTokenSilently, getIdTokenClaims, endSessionIfExpired]);

  useEffect(() => {
    if (!isRestoring || isLoading) return;

    (async () => {
      if (isAuthenticated) await renew();
      else endSessionIfExpired();

      setIsRestoring(false);
    })();
  }, [isRestoring, isLoading, isAuthenticated, renew, endSessionIfExpired]);

  useEffect(() => {
    if (!isRestoring) return;

    const timer = setTimeout(() => setIsRestoring(false), RESTORE_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isRestoring]);

  useEffect(() => {
    if (isRestoring || !isAuthenticated) return;

    const interval = setInterval(() => {
      if (isTokenExpired(session.getValue(), RENEWAL_WINDOW_MS)) renew();
    }, RENEWAL_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isRestoring, isAuthenticated, renew]);

  return {
    isRestoring,
    loginRedirect: wasAuth0Session || isAuthenticated ? AUTH0_LOGIN_PATH : undefined,
  };
};

export default useAuth0SessionSync;
